import React, { useState } from "react";
import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import { IoCheckboxOutline, IoSquareOutline } from "react-icons/io5";

// Design tokens
import { HEATMAP_COLORS } from "utils/designTokens";

// Note: This component renders only the heatmap content.
function PublishingHeatMap({ data, colorScheme }) {
  const [showHighlights, setShowHighlights] = useState(true);

  // Use provided colorScheme or fallback to design tokens
  const defaultColorScheme = HEATMAP_COLORS.default;
  const colors = colorScheme || defaultColorScheme;

  // Function to determine color based on value
  const getIntensityColor = (value) => {
    let background;

    // Base colors - lighter/brighter
    const baseColors = [colors.low, colors.medium, colors.high, colors.veryHigh];
    
    if (value > 12) {
      background = baseColors[3];
    } else if (value > 8) {
      background = baseColors[2];
    } else if (value > 4) {
      background = baseColors[1];
    } else if (value > 0) {
      background = baseColors[0];
    } else {
      background = "rgba(255, 255, 255, 0.05)";
    }

    return background;
  };

  const truncateCompetitorName = (name) => {
    if (name && name.length > 17) {
      return `${name.substring(0, 17)}...`;
    }
    return name;
  };

  // Find dominant competitor for each time slot
  const getDominantCompetitor = (weekday, timeSlot) => {
    let maxValue = 0;
    let dominant = null;

    // Loop through all competitors for this time slot
    Object.entries(data.heatmap[weekday][timeSlot]).forEach(([competitor, value]) => {
      if (value > maxValue) {
        maxValue = value;
        dominant = truncateCompetitorName(competitor);
      }
    });

    return { competitor: dominant, value: maxValue };
  };

  return (
    <VuiBox sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <VuiBox mb={2} display="flex" alignItems="center" justifyContent="space-between">
        <VuiBox />
        <VuiButton
          variant="text"
          color="info"
          onClick={() => setShowHighlights(!showHighlights)}
          startIcon={showHighlights ? <IoCheckboxOutline /> : <IoSquareOutline />}
          sx={{ padding: 0 }}
        >
          <VuiTypography variant="button" color="info" fontWeight="regular">
            Show Dominant
          </VuiTypography>
        </VuiButton>
      </VuiBox>

      <VuiBox mb={2} display="flex" alignItems="center" justifyContent="center" gap={3}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBox width="10px" height="10px" sx={{ background: colors.low }} borderRadius="2px" />
          <VuiBox width="10px" height="10px" sx={{ background: colors.medium }} borderRadius="2px" />
          <VuiBox width="10px" height="10px" sx={{ background: colors.high }} borderRadius="2px" />
          <VuiBox width="10px" height="10px" sx={{ background: colors.veryHigh }} borderRadius="2px" />
          <VuiTypography variant="caption" color="white" ml={1}>Low → High</VuiTypography>
        </VuiBox>
      </VuiBox>

      <VuiBox overflow="auto" mb={2} sx={{ flexGrow: 1 }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "3px" }}>
          <thead>
          <tr>
            <th style={{
              width: "80px",
              textAlign: "left",
              padding: "8px 8px",
              color: "rgba(255,255,255,0.7)",
              fontWeight: "normal",
              background: "transparent",
            }}>
              <VuiTypography variant="caption" color="white" fontWeight="medium">Weekday</VuiTypography>
            </th>
            {data.timeSlots.map((slot, index) => (
              <th key={index} style={{
                textAlign: "center",
                padding: "8px 4px",
                color: "rgba(255,255,255,0.7)",
                minWidth: "60px",
                fontWeight: "normal",
                background: "transparent",
              }}>
                <VuiTypography variant="caption" color="white" fontWeight="medium">{slot}</VuiTypography>
              </th>
            ))}
          </tr>
          </thead>
          <tbody>
          {data.weekdays.map((day, rowIndex) => (
            <tr key={rowIndex}>
              <td style={{
                textAlign: "left",
                padding: "6px 8px",
                color: "white",
                fontWeight: "medium",
                background: "transparent",
                borderRadius: "4px",
              }}>
                <VuiTypography variant="caption" color="white" fontWeight="medium">{day}</VuiTypography>
              </td>
              {data.timeSlots.map((slot, colIndex) => {
                // Calculate total value for all competitors for this slot
                let totalValue = 0;
                Object.values(data.heatmap[day][slot]).forEach(val => {
                  totalValue += val;
                });

                // Get color based on intensity
                const colorStyle = getIntensityColor(totalValue);

                // Get dominant competitor for this slot
                const dominant = getDominantCompetitor(day, slot);

                return (
                  <td
                    key={colIndex}
                    style={{
                      textAlign: "center",
                      padding: "4px 2px",
                      background: colorStyle,
                      color: "white",
                      borderRadius: "4px",
                      position: "relative",
                      height: "52px",
                      minWidth: "55px",
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: "16px" }}>
                      {totalValue > 0 ? totalValue : "-"}
                    </span>
                    {showHighlights && dominant.value > 0 && (
                      <div style={{
                        fontSize: "9px",
                        position: "absolute",
                        bottom: "3px",
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        color: "white",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        padding: "0 2px",
                      }}>
                        {dominant.competitor}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          </tbody>
        </table>
      </VuiBox>
    </VuiBox>
  );
}

PublishingHeatMap.propTypes = {
  data: PropTypes.object.isRequired,
  colorScheme: PropTypes.object,
};

export default PublishingHeatMap; 